
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, Sparkles, Droplets, Sun, Activity } from "lucide-react";

const Conditions = () => {
  const conditions = [
    {
      name: "Fine Lines & Wrinkles",
      description: "Smooth and prevent signs of aging with targeted treatments that restore youthful skin.",
      treatments: ["Botox", "Dermal Fillers", "Skin Boosters"],
      icon: Zap,
      slug: "fine-lines-wrinkles",
      symptoms: "Expression lines, crow's feet, forehead wrinkles",
      howWeHelp: "Our neuromodulators and fillers target specific muscle movements and volume loss to create natural-looking results."
    },
    {
      name: "Acne & Scars",
      description: "Clear skin and reduce scarring with advanced therapies designed for lasting results.",
      treatments: ["VI Peel", "RF Microneedling", "Laser Facial"],
      icon: Shield,
      slug: "acne-scars",
      symptoms: "Active breakouts, acne scarring, uneven texture",
      howWeHelp: "We combine chemical peels, microneedling, and laser technology to address both active acne and improve scar appearance."
    },
    {
      name: "Pigmentation & Spots",
      description: "Even skin tone and reduce dark spots for a radiant, uniform complexion.",
      treatments: ["Laser Facial", "VI Peel", "IPL"],
      icon: Sun,
      slug: "pigmentation-spots",
      symptoms: "Sun spots, melasma, uneven pigmentation",
      howWeHelp: "Our advanced laser and chemical peel treatments target excess melanin to reveal clearer, more even-toned skin."
    },
    {
      name: "Uneven Tone & Texture",
      description: "Smooth and refine skin texture for a radiant, healthy glow.",
      treatments: ["AquaFirme", "Dermaplaning", "RF Microneedling"],
      icon: Sparkles,
      slug: "uneven-tone-texture",
      symptoms: "Rough texture, enlarged pores, dullness",
      howWeHelp: "We use gentle exfoliation and collagen-stimulating treatments to resurface and refine your skin's texture."
    },
    {
      name: "Dull & Dehydrated Skin",
      description: "Restore hydration and luminosity to tired, lackluster skin.",
      treatments: ["Skin Boosters", "AquaFirme", "IV Therapy"],
      icon: Droplets,
      slug: "dull-dehydrated-skin",
      symptoms: "Lack of radiance, fine lines from dehydration, tired appearance",
      howWeHelp: "Our hydrating treatments infuse moisture deep into the skin while supporting overall wellness from within."
    },
    {
      name: "Body Fat & Double Chin",
      description: "Non-surgical fat reduction for a defined profile and enhanced confidence.",
      treatments: ["Kybella", "CoolSculpting", "Ultrasound Therapy"],
      icon: Activity,
      slug: "body-fat-double-chin",
      symptoms: "Submental fat, loose skin, undefined jawline",
      howWeHelp: "We offer FDA-approved treatments that permanently eliminate fat cells for natural-looking contouring results."
    }
  ];

  return (
    <Layout>
      <section className="py-20 bg-juv-cream-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-juv-deep-chestnut mb-6 font-juv-display">
              Conditions We Treat
            </h1>
            <p className="text-xl text-juv-deep-chestnut/70 leading-relaxed">
              Whatever your skin concern, we have advanced treatments to help you achieve your goals naturally and safely.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {conditions.map((condition, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-luxury hover:shadow-editorial transition-all duration-300 hover:-translate-y-2 border border-juv-rose-clay/20 group"
              >
                <div className="mb-6 text-center">
                  <div className="w-16 h-16 bg-juv-soft-blush rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <condition.icon className="w-8 h-8 text-juv-terracotta" />
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-juv-deep-chestnut mb-4 font-juv-display text-center">
                  {condition.name}
                </h3>
                
                <p className="text-juv-deep-chestnut/70 mb-4 leading-relaxed text-center">
                  {condition.description}
                </p>
                
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-juv-terracotta mb-2 uppercase tracking-wide">
                    Common Symptoms
                  </h4>
                  <p className="text-sm text-juv-deep-chestnut/60 leading-relaxed">
                    {condition.symptoms}
                  </p>
                </div>
                
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-juv-terracotta mb-2 uppercase tracking-wide">
                    How JUV Helps
                  </h4>
                  <p className="text-sm text-juv-deep-chestnut/60 leading-relaxed mb-4">
                    {condition.howWeHelp}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {condition.treatments.map((treatment, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-juv-soft-blush text-juv-deep-chestnut text-sm rounded-full"
                      >
                        {treatment}
                      </span>
                    ))}
                  </div>
                </div>
                
                <Button 
                  asChild
                  className="w-full bg-juv-terracotta hover:bg-juv-deep-chestnut text-white rounded-full"
                >
                  <Link to={`/conditions/${condition.slug}`} className="flex items-center justify-center gap-2">
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            ))}
          </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <div className="bg-gradient-to-br from-juv-soft-blush to-juv-rose-clay/30 rounded-2xl p-12">
              <Sparkles className="w-12 h-12 text-juv-terracotta mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-juv-deep-chestnut mb-6">
                Not Sure Which Condition Applies to You?
              </h2>
              <p className="text-xl text-juv-deep-chestnut mb-8">
                Take our personalized quiz to identify your skin concerns and get matched with the perfect treatments.
              </p>
              <Button 
                asChild
                size="lg" 
                className="bg-juv-terracotta hover:bg-juv-deep-chestnut text-white px-8 py-4 rounded-full"
              >
                <Link to="/quiz">Take the Glow Quiz</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Conditions;
